import { queryClient } from "@/lib/queryClient";
import { useCallback, useEffect, useRef, useState } from "react";

import { currentReturnPath } from "./auth-utils";

export type OAuthProvider = "apple" | "google" | "facebook";

/** What the callback page posts back before closing (server: `popupResponse`). */
type AuthMessage =
  | { type: "bday-auth"; ok: true }
  | { type: "bday-auth"; ok: false; error: string };

function isAuthMessage(data: unknown): data is AuthMessage {
  if (typeof data !== "object" || data === null) return false;
  const m = data as { type?: unknown; ok?: unknown };
  return m.type === "bday-auth" && typeof m.ok === "boolean";
}

const POPUP_FEATURES = "width=520,height=680,menubar=no,toolbar=no,location=yes,resizable=yes";

/**
 * Some in-app webviews (Facebook, Instagram, Gmail) return a truthy Window from
 * `window.open` that never navigates and never reports `closed`. Without a
 * ceiling the spinner would sit there forever with all three buttons disabled.
 */
const POPUP_WATCHDOG_MS = 120_000;

/**
 * Run a social sign-in without unloading the page underneath.
 *
 * The popup points at OUR OWN `/api/public/auth/<provider>` endpoint, not at a
 * provider SDK: the server already owns the state cookie, the token exchange and
 * the session, so there is nothing a third-party script would add beyond three
 * more origins to trust. The callback answers the popup with a page that
 * `postMessage`s us and closes; we just refresh the auth query and the tree
 * re-renders signed in, with every bit of React state on the page intact.
 *
 * Legacy did this with the Google/Facebook/Apple JS SDKs. Same effect, except
 * the whole exchange stays first-party — which also means nothing here depends
 * on third-party cookies, and those have only got more restricted since.
 *
 * If the popup is blocked we fall back to the full-page redirect, which is why
 * the server takes the same `next` either way — unless `inPlaceOnly` (the in-flow
 * authwall over the composer, 2026-10-23): then nothing navigates, `blocked` names the
 * provider, and the caller asks the member to allow pop-ups and retry (`redirect` is the
 * explicit, member-chosen way out).
 */
export function useOAuthPopup(
  opts: { link?: boolean; onSuccess?: () => void; inPlaceOnly?: boolean } = {},
) {
  const inPlaceOnly = !!opts.inPlaceOnly;
  // Link mode = settings "Connect": the server attaches the identity to the signed-in
  // member instead of signing anyone in (`?link=1`, routes/public/oauth.ts).
  const link = !!opts.link;
  const onSuccessRef = useRef(opts.onSuccess);
  onSuccessRef.current = opts.onSuccess;
  const [pending, setPending] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState("");
  // Bumped when a popup closes without ever reporting success.
  const [cancelled, setCancelled] = useState(0);
  // Provider whose popup the browser refused (inPlaceOnly mode only).
  const [blocked, setBlocked] = useState<OAuthProvider | null>(null);
  // Held in a ref so the message listener and the close-poller can both reach
  // the live window without re-subscribing on every render.
  const popupRef = useRef<Window | null>(null);
  const succeededRef = useRef(false);

  const cleanup = useCallback(() => {
    popupRef.current = null;
    setPending(null);
  }, []);

  /**
   * Re-read the session whatever happened.
   *
   * The popup can close without its message ever reaching us — `event.source` is
   * allowed to be null once the sender has closed, a listener can be unmounted
   * mid-flow, and a 403 (banned/locked) renders raw JSON and never posts. The
   * cookie is set server-side regardless, so without this the member would be
   * signed in but the SPA would render logged-out until a manual reload, with no
   * way back. One cheap request makes every failure mode self-healing.
   */
  const refreshSession = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["/api/user/auth/me"] });
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Identity check is `event.source`, not `event.origin`: we compare against
      // the exact window handle we opened, which is stronger than an origin test
      // and doesn't break in local dev, where the app is on Vite's :5173 but the
      // callback document comes back on BASE_URL's :5001.
      //
      // Nothing here trusts the message for authority anyway — the session is an
      // httpOnly cookie the callback response already set, and all this does is
      // refetch it. A spoofed message could at worst cause a redundant refetch.
      if (!popupRef.current || event.source !== popupRef.current) return;
      if (!isAuthMessage(event.data)) return;
      if (event.data.ok) {
        setError("");
        succeededRef.current = true;
        // No navigation: __root re-renders off the refreshed auth query, which
        // is also what performs the `?next=` hop when we're on an auth page.
        refreshSession();
        onSuccessRef.current?.();
      } else {
        setError(event.data.error);
      }
      try {
        popupRef.current?.close();
      } catch {
        // Already gone.
      }
      cleanup();
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      // Don't strand a popup whose opener has gone: AuthSocial is conditionally
      // rendered (the forgot stage drops it, dialogs close), and a live popup
      // with no listener left would complete into the void.
      try {
        popupRef.current?.close();
      } catch {
        // Already gone.
      }
      popupRef.current = null;
    };
  }, [cleanup, refreshSession]);

  // The user can dismiss the popup without finishing, and some webviews never
  // report closing at all — poll for the first, cap the second.
  useEffect(() => {
    if (!pending) return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const win = popupRef.current;
      if (win && win.closed) {
        window.clearInterval(timer);
        // Closed without a message reaching us — it may still have succeeded.
        refreshSession();
        if (link) onSuccessRef.current?.();
        if (!succeededRef.current) setCancelled((n) => n + 1);
        cleanup();
        return;
      }
      if (Date.now() - startedAt > POPUP_WATCHDOG_MS) {
        window.clearInterval(timer);
        try {
          win?.close();
        } catch {
          // Already gone.
        }
        refreshSession();
        cleanup();
      }
    }, 400);
    return () => window.clearInterval(timer);
  }, [pending, cleanup, refreshSession]);

  const start = useCallback(
    (provider: OAuthProvider) => {
      const next = encodeURIComponent(currentReturnPath());
      const url = `/api/public/auth/${provider}?next=${next}${link ? "&link=1" : ""}`;
      setError("");
      setBlocked(null);
      // `window.open` has to be called synchronously inside the click handler or
      // the popup blocker eats it. The window name is unique per attempt so two
      // tabs mid-sign-in don't reuse — and post back into — each other's popup.
      succeededRef.current = false;
      const win = window.open(
        `${url}&popup=1`,
        `bday-oauth-${Date.now().toString(36)}`,
        POPUP_FEATURES,
      );
      // Blocked outright, or blocked-but-returning-a-corpse — take the old route, or in
      // place-only mode say so and let the member retry (a fresh click may be allowed).
      if (!win || win.closed) {
        if (inPlaceOnly) {
          setBlocked(provider);
          return;
        }
        window.location.href = url;
        return;
      }
      popupRef.current = win;
      setPending(provider);
      win.focus();
    },
    [link, inPlaceOnly],
  );

  /** Explicit full-page sign-in for a blocked popup (member's choice; `next` returns here). */
  const redirect = useCallback(
    (provider: OAuthProvider) => {
      const next = encodeURIComponent(currentReturnPath());
      window.location.href = `/api/public/auth/${provider}?next=${next}${link ? "&link=1" : ""}`;
    },
    [link],
  );

  return {
    start,
    pending,
    error,
    cancelled,
    blocked,
    redirect,
    clearError: () => setError(""),
  };
}
